import { useEffect, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/** Resolve in-app screen name from URL search (?page=X). Missing = Landing. */
function pageFromLocationSearch(search) {
  try {
    const q = search && typeof search === "string" ? search : "";
    const rawQuery = q.startsWith("?") ? q.slice(1) : q;
    const params = new URLSearchParams(rawQuery);
    const raw = params.get("page");
    const trimmed = raw && raw.trim();
    return trimmed ? trimmed : "LandingPage";
  } catch {
    return "LandingPage";
  }
}

/**
 * Keeps Browser History + `?page=<ScreenName>` aligned with Home's selectedPage so refresh/back work.
 * Only applies under `/`; `/qr/*` is handled by explicit effects in Home.page.js.
 *
 * URL → state runs in useLayoutEffect so after Back/Forward, selectedPage updates before useEffect
 * can push the URL using stale state (prevents oscillation / blinking).
 */
export const useNavigationSync = (selectedPage, setSelectedPage) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isHomeShellPath = location.pathname === "/" || location.pathname === "";

  useLayoutEffect(() => {
    if (!isHomeShellPath) return;
    const nextPage = pageFromLocationSearch(location.search);
    setSelectedPage((prev) => (prev === nextPage ? prev : nextPage));
  }, [isHomeShellPath, location.search, setSelectedPage]);

  useEffect(() => {
    if (!isHomeShellPath) return;

    const urlPage = pageFromLocationSearch(location.search);
    if (urlPage === selectedPage) return;

    if (selectedPage === "LandingPage") {
      const qs = location.search.startsWith("?") ? location.search.slice(1) : location.search || "";
      const params = new URLSearchParams(qs);
      if (!params.has("page")) return;
      params.delete("page");
      const rest = params.toString();
      navigate(rest ? `/?${rest}` : "/", { replace: false });
      return;
    }

    navigate(`/?page=${encodeURIComponent(selectedPage)}`, { replace: false });
  }, [selectedPage, navigate, isHomeShellPath]);
};
